import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Paper, Typography, Box, Grid, TextField, Button, Alert, CircularProgress, IconButton, Chip, Stack, FormControlLabel, Checkbox, FormGroup, FormLabel } from '@mui/material';
import ProfessionalPhotoField from '../components/pro/ProfessionalPhotoField';
import PhoneField from '../components/common/PhoneField';
import { ArrowBack, Save } from '@mui/icons-material';
import realtorService from '../services/realtorService';
import EacAgencyPicker from '../components/realtor/EacAgencyPicker';
import { brand, text, shadow } from '../theme/colors';

const RealtorProfilePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({ displayName: '', phone: '', bio: '', operatingCities: '', agencyId: null, agencyNameClaimed: '', sells: [], yearsExperience: '' });
  const [photo, setPhoto] = useState(null);
  const [agency, setAgency] = useState(null);

  useEffect(() => {
    realtorService.getMyProfile()
      .then((res) => {
        const p = res.data;
        setForm({
          displayName: p.display_name || '', phone: p.phone || '', bio: p.bio || '',
          operatingCities: (p.operating_cities || []).join(', '),
          agencyId: p.eac_agency_id || null, agencyNameClaimed: p.agency_name_claimed || p.agency?.name || '',
          sells: Array.isArray(p.sells) ? p.sells : [], yearsExperience: p.years_experience ?? '',
        });
        setPhoto(p.photo_display_url || null);
        setAgency(p.agency || null);
      })
      .catch((err) => { if (err.response?.status !== 404) setError('Failed to load your profile'); })
      .finally(() => setLoading(false));
  }, []);

  const search = useCallback((q) => realtorService.matchAgencies(q), []);

  const handleSave = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const res = await realtorService.saveMyProfile({
        displayName: form.displayName.trim(),
        phone: form.phone.trim() || null,
        bio: form.bio || null,
        operatingCities: form.operatingCities.split(',').map((c) => c.trim()).filter(Boolean),
        agencyId: form.agencyId,
        agencyNameClaimed: form.agencyNameClaimed.trim() || null,
        sells: form.sells,
        yearsExperience: form.yearsExperience === '' ? null : Number(form.yearsExperience),
      });
      setAgency(res.data.agency || null);
      setSuccess('Profile saved');
    } catch (err) {
      if (err.response?.data?.errorCode === 'AGENCY_NOT_FOUND') {
        setForm((f) => ({ ...f, agencyId: null }));
        setAgency(null);
      }
      setError(err.response?.data?.error || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Container sx={{ py: 6, textAlign: 'center' }}><CircularProgress sx={{ color: brand.primary }} /></Container>;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/dashboard')} sx={{ color: text.secondary }}><ArrowBack /></IconButton>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: text.primary }}>Realtor profile</Typography>
          <Typography variant="body2" sx={{ color: text.secondary }}>Your details and the EAC-registered agency you work for</Typography>
        </Box>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      <Paper sx={{ p: 3, boxShadow: shadow.md, borderRadius: 2 }}>
        <Box sx={{ mb: 3 }}>
          <ProfessionalPhotoField previewUrl={photo} name={form.displayName}
            onUploaded={async (url) => {
              const r = await realtorService.saveMyProfile({ displayName: form.displayName.trim() || 'Realtor', photoUrl: url });
              setPhoto(r.data?.photo_display_url || null); setSuccess('Photo saved');
            }} />
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Display name" value={form.displayName} required
              onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={6}>
            <PhoneField label="Phone" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </Grid>
          <Grid item xs={12}>
            <EacAgencyPicker
              value={{ agencyId: form.agencyId, agencyNameClaimed: form.agencyNameClaimed }}
              onChange={({ agencyId, agencyNameClaimed, agency: a }) => { setForm((f) => ({ ...f, agencyId, agencyNameClaimed })); setAgency(a); }}
              search={search}
            />
            {agency && (
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Chip size="small" variant="outlined" label={agency.address} />
                <Chip size="small" variant="outlined" label={agency.status} />
              </Stack>
            )}
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Cities you operate in" value={form.operatingCities} helperText="Comma-separated, e.g. Harare, Bulawayo"
              onChange={(e) => setForm({ ...form, operatingCities: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={8}>
            <FormLabel sx={{ fontSize: 14 }}>What you sell</FormLabel>
            <FormGroup row>
              {[['house', 'Houses'], ['stand', 'Stands']].map(([v, l]) => (
                <FormControlLabel key={v} label={l} control={<Checkbox checked={form.sells.includes(v)}
                  onChange={(e) => setForm({ ...form, sells: e.target.checked ? [...form.sells, v] : form.sells.filter((x) => x !== v) })} />} />
              ))}
            </FormGroup>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth type="number" label="Years as an estate agent" value={form.yearsExperience} inputProps={{ min: 0, max: 70 }}
              onChange={(e) => setForm({ ...form, yearsExperience: e.target.value })} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={3} label="About you" value={form.bio} helperText="Buyers read this on your card: your areas, what you specialise in, how you work with buyers abroad."
              onChange={(e) => setForm({ ...form, bio: e.target.value })} />
          </Grid>
          <Grid item xs={12} sx={{ textAlign: 'right' }}>
            <Button variant="contained" startIcon={<Save />} disabled={saving || form.displayName.trim().length < 2} onClick={handleSave}
              sx={{ background: brand.gradient, fontWeight: 600, '&:hover': { background: brand.gradientHover } }}>
              {saving ? 'Saving…' : 'Save profile'}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default RealtorProfilePage;
